import * as React from 'react'

import { observer } from 'mobx-react'

import { LocationType } from '~/client/graph'
import { useValidateLocationRestrictionsForTimePeriodsQuery } from '~/client/graph/operations/generated/LocationType.generated'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { useEquipmentResourceStore } from '~/client/src/shared/hooks/useEquipmentResourceStore'

import SitePermitCreationFormStore from '../../../../../WorkflowCreationForm.store'
import FormResourcePicker from './FormResourcePicker'
import { collectOverlapConflicts } from './overlapMessages'

const getEquipmentLocationType = () => LocationType.OffloadingEquipment

interface IProps {
  store: SitePermitCreationFormStore
  projectId?: string
  startCreatingRef?: React.MutableRefObject<(() => void) | null>
  onClose?: () => void
  startDate: number | null
  endDate: number | null
  permitTypeId: string | null
  permitId: string | null
  isAdminOrFormsMaster: boolean
  timePeriods: Array<{ startDate: number; endDate: number }>
}

const FormEquipmentPicker: React.FC<IProps> = ({
  store,
  projectId,
  startCreatingRef,
  onClose,
  startDate,
  endDate,
  permitTypeId,
  permitId,
  isAdminOrFormsMaster,
  timePeriods,
}) => {
  const { records, loading, schema } = useEquipmentResourceStore(projectId)

  const equipmentIds = React.useMemo(() => records.map(r => r.id), [records])

  const skip =
    !startDate ||
    !endDate ||
    !permitTypeId ||
    !equipmentIds.length ||
    !timePeriods.length

  const { data, loading: restrictionsLoading } =
    useValidateLocationRestrictionsForTimePeriodsQuery({
      variables: {
        timePeriods,
        workflowId: permitTypeId as string,
        locationIds: equipmentIds,
        permitId,
      },
      skip,
    })

  // Pessimistic gate: prevents selecting a row before in-flight validation flags it.
  const isCheckingRestrictions = !skip && restrictionsLoading

  const overlapConflicts = React.useMemo(
    () =>
      collectOverlapConflicts(data?.validateLocationRestrictionsForTimePeriods),
    [data],
  )

  // Read both the readiness flag and the map reference here so this observer
  // tracks them: setFreshResourcePermittedCompanies assigns a new Map on every
  // update, so referencing it makes the picker re-render (and recompute the
  // callbacks below) when permittedCompanies change live while the modal is
  // open — FormResourcePicker, which invokes the callbacks, is not an observer.
  const isEquipmentControlsReady = store.areResourceControlsReadyForType(
    ResourceTypeId.OFFLOADING_EQUIPMENT,
  )
  const freshResourcePermittedCompanies = store.freshResourcePermittedCompanies

  // Company-restriction now comes from the store's fresh resource source
  // (registered by ResourceRestrictionsSync), the same source save validation
  // uses — no local reserved-for map, so picker and validation can't diverge.
  const getNote = React.useCallback(
    (recordId: string) => store.getResourceNote(recordId, overlapConflicts),
    // freshResourcePermittedCompanies drives recompute on live restriction edits
    [overlapConflicts, store, freshResourcePermittedCompanies],
  )

  const isItemDisabled = React.useCallback(
    (recordId: string) => {
      if (isAdminOrFormsMaster) return false
      if (isCheckingRestrictions) return true
      // Pessimistic until the equipment controls have settled, so a restricted
      // row is never briefly shown as enabled before sync commits.
      if (!isEquipmentControlsReady) return true
      return getNote(recordId)?.tone === 'blocked'
    },
    [
      getNote,
      isAdminOrFormsMaster,
      isCheckingRestrictions,
      isEquipmentControlsReady,
    ],
  )

  return (
    <FormResourcePicker
      store={store}
      resourceTypeId={ResourceTypeId.OFFLOADING_EQUIPMENT}
      getLocationType={getEquipmentLocationType}
      records={records}
      loading={loading}
      projectId={projectId}
      defaultName="New Equipment"
      startCreatingRef={startCreatingRef}
      onClose={onClose}
      isItemDisabled={isItemDisabled}
      getNote={getNote}
      recordPredicate={store.resourceVisibilityPredicate}
      resourceSchema={schema}
    />
  )
}

export default observer(FormEquipmentPicker)
